import { useState, useCallback } from 'react';
import { ArrowLeft, ArrowRight, Rocket, Zap, Info, Heart, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

import { EngiCoroLogo } from '@/components/workflow/EngiCoroLogo';
import { BRAND_NAVY_VAR, BRAND_SIDEBAR_VAR } from '@/constants/brandColors';
import {
  WORKFLOW_TEMPLATES,
  ADVANCED_TEMPLATE_ID,
  getAiHoningPrompt,
  getBuildGuidance,
  WORKFLOW_PROFILE_KEY,
  type WorkflowProfile,
  type WorkflowTemplate,
} from '@/lib/workflowTemplates';
import ReactMarkdown from 'react-markdown';

function TipJarPopup({ open, onClose }: { open: boolean; onClose: () => void }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/70" style={{ contain: 'strict' }} onClick={onClose}>
      <div
        className="w-full max-w-md rounded-t-2xl border-t border-border bg-background shadow-2xl overflow-hidden animate-in slide-in-from-bottom duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-muted/50">
          <div className="flex items-center gap-2">
            <Heart className="w-4 h-4 text-pink-500" />
            <h3 className="text-sm font-semibold">Support EngiCoro</h3>
          </div>
          <button onClick={onClose} className="p-2 text-muted-foreground"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4 max-h-[60dvh] overflow-y-auto">
          <p className="text-sm text-muted-foreground text-center leading-relaxed">
            EngiCoro Flow Weaver is built with passion. If you find it useful, consider supporting the project!
          </p>
          <div className="flex flex-col items-center gap-3">
            <img src="/paypal-qr.png" alt="PayPal QR Code" className="w-40 h-40 rounded-lg border border-border object-contain" />
            <p className="text-xs text-muted-foreground">Scan with your phone to tip via PayPal</p>
          </div>
          <a
            href="https://paypal.me/arentb"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 p-4 rounded-lg border border-border hover:border-primary/50 transition-all"
          >
            <div className="w-10 h-10 rounded-lg bg-[#0070ba] flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-xs">PP</span>
            </div>
            <div className="min-w-0">
              <h4 className="text-sm font-semibold">PayPal</h4>
              <p className="text-[11px] text-muted-foreground">Open PayPal</p>
            </div>
            <ArrowRight className="w-4 h-4 text-muted-foreground ml-auto flex-shrink-0" />
          </a>
        </div>
      </div>
    </div>
  );
}

interface MobileWizardDialogProps {
  open: boolean;
  onComplete: (profile: WorkflowProfile | null) => void;
}

type Step = 'choose' | 'q1' | 'q2' | 'guidance';

export function MobileWizardDialog({ open, onComplete }: MobileWizardDialogProps) {
  const [step, setStep] = useState<Step>('choose');
  const [selectedTemplate, setSelectedTemplate] = useState<WorkflowTemplate | null>(null);
  const [q1Answer, setQ1Answer] = useState('');
  const [q2Answer, setQ2Answer] = useState('');
  const [tipJarOpen, setTipJarOpen] = useState(false);

  const handleSelectTemplate = useCallback((tpl: WorkflowTemplate) => {
    setSelectedTemplate(tpl);
    setQ1Answer('');
    setQ2Answer('');
    setStep('q1');
  }, []);

  const handleAdvanced = useCallback(() => {
    const profile: WorkflowProfile = {
      templateId: ADVANCED_TEMPLATE_ID,
      q1: '', q2: '', aiHoningPrompt: '', guidance: '',
    };
    localStorage.setItem(WORKFLOW_PROFILE_KEY, JSON.stringify(profile));
    onComplete(profile);
  }, [onComplete]);

  const handleQ1 = useCallback((answerId: string) => { setQ1Answer(answerId); setStep('q2'); }, []);
  const handleQ2 = useCallback((answerId: string) => { setQ2Answer(answerId); setStep('guidance'); }, []);

  const handleFinish = useCallback(() => {
    if (!selectedTemplate) return;
    const profile: WorkflowProfile = {
      templateId: selectedTemplate.id,
      q1: q1Answer, q2: q2Answer,
      aiHoningPrompt: getAiHoningPrompt(selectedTemplate.id, q1Answer, q2Answer),
      guidance: getBuildGuidance(selectedTemplate.id, q1Answer, q2Answer),
    };
    localStorage.setItem(WORKFLOW_PROFILE_KEY, JSON.stringify(profile));
    onComplete(profile);
  }, [selectedTemplate, q1Answer, q2Answer, onComplete]);

  const handleBack = useCallback(() => {
    if (step === 'q1') { setStep('choose'); setSelectedTemplate(null); }
    else if (step === 'q2') { setStep('q1'); setQ2Answer(''); }
    else if (step === 'guidance') setStep('q2');
  }, [step]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-background flex flex-col" style={{ height: '100dvh' }} data-testid="mobile-wizard">
      {/* Compact header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-border flex-shrink-0" style={{ background: BRAND_SIDEBAR_VAR, paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <EngiCoroLogo className="h-10 flex-shrink-0" />
        <p className="text-[10px] flex-1" style={{ color: BRAND_NAVY_VAR, opacity: 0.5 }}>Choose your workflow type</p>
      </div>

      {/* Scrollable content — min-h-0 required for flex-1 to constrain height */}
      <div className="flex-1 overflow-y-auto min-h-0" style={{ WebkitOverflowScrolling: 'touch' }}>
        <div className="p-4 pb-8">
          {step === 'choose' && (
            <div className="space-y-3">
              <div className="text-center mb-4">
                <h2 className="text-lg font-bold mb-1">What are you building?</h2>
                <p className="text-xs text-muted-foreground">Select a workflow type</p>
              </div>

              <div className="space-y-2">
                {WORKFLOW_TEMPLATES.map((tpl) => (
                  <button
                    key={tpl.id}
                    onClick={() => handleSelectTemplate(tpl)}
                    className="flex items-start gap-3 w-full p-4 rounded-xl border border-border bg-card active:bg-primary/10 transition-colors text-left"
                    style={{ minHeight: '56px' }}
                    data-testid={`mobile-wizard-template-${tpl.id}`}
                  >
                    <span className="text-2xl flex-shrink-0 mt-0.5">{tpl.icon}</span>
                    <div className="min-w-0 flex-1">
                      <h3 className="font-semibold text-sm">{tpl.title}</h3>
                      <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{tpl.subtitle}</p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-1" />
                  </button>
                ))}
              </div>

              {/* Advanced option */}
              <div className="pt-2 border-t border-border mt-3">
                <button
                  onClick={handleAdvanced}
                  className="w-full flex items-center gap-3 p-4 rounded-xl border border-dashed border-border active:bg-primary/10 transition-colors text-left"
                  style={{ minHeight: '56px' }}
                  data-testid="mobile-wizard-advanced"
                >
                  <Zap className="w-6 h-6 text-muted-foreground flex-shrink-0" />
                  <div className="flex-1">
                    <h3 className="font-semibold text-sm">Advanced — Blank Canvas</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">Skip guidance. You know what you're doing.</p>
                  </div>
                </button>
              </div>
            </div>
          )}

          {step === 'q1' && selectedTemplate && (
            <MobileQuestionStep template={selectedTemplate} questionIndex={0} onSelect={handleQ1} onBack={handleBack} stepLabel="Step 1 of 2" />
          )}
          {step === 'q2' && selectedTemplate && (
            <MobileQuestionStep template={selectedTemplate} questionIndex={1} onSelect={handleQ2} onBack={handleBack} stepLabel="Step 2 of 2" />
          )}

          {step === 'guidance' && selectedTemplate && (
            <div className="space-y-4">
              <button onClick={handleBack} className="flex items-center gap-1 text-xs text-muted-foreground active:text-foreground p-2 -ml-2">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <div className="text-center">
                <span className="text-3xl">{selectedTemplate.icon}</span>
                <h2 className="text-lg font-bold mt-2">{selectedTemplate.title}</h2>
                <p className="text-xs text-muted-foreground mt-1">Here's how to build this</p>
              </div>
              <div className="rounded-xl border border-border bg-muted/30 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Info className="w-4 h-4 text-primary" />
                  <h3 className="text-sm font-semibold">Build Guide</h3>
                </div>
                <div className="prose prose-sm dark:prose-invert max-w-none text-sm leading-relaxed">
                  <ReactMarkdown>{getBuildGuidance(selectedTemplate.id, q1Answer, q2Answer)}</ReactMarkdown>
                </div>
              </div>
              <div className="rounded-xl border border-primary/20 bg-primary/5 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Rocket className="w-4 h-4 text-primary" />
                  <h3 className="text-sm font-semibold text-primary">AI Assistant is tuned</h3>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  The AI assistant has been configured for <strong>{selectedTemplate.title}</strong> workflows.
                </p>
              </div>
              <Button onClick={handleFinish} className="w-full gap-2 h-12 text-base" size="lg" data-testid="mobile-wizard-start">
                <Rocket className="w-5 h-5" /> Start Building
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-center px-4 py-3 border-t border-border bg-muted/30 flex-shrink-0" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
        <button onClick={() => setTipJarOpen(true)} className="flex items-center gap-1.5 text-xs text-muted-foreground active:text-pink-500 p-2">
          <Heart className="w-3.5 h-3.5" /> Support the project
        </button>
      </div>
      <TipJarPopup open={tipJarOpen} onClose={() => setTipJarOpen(false)} />
    </div>
  );
}

function MobileQuestionStep({ template, questionIndex, onSelect, onBack, stepLabel }: {
  template: WorkflowTemplate; questionIndex: 0 | 1; onSelect: (id: string) => void; onBack: () => void; stepLabel: string;
}) {
  const q = template.questions[questionIndex];
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="flex items-center gap-1 text-xs text-muted-foreground active:text-foreground p-2 -ml-2">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <span className="text-[11px] text-muted-foreground font-medium">{stepLabel}</span>
      </div>
      <div className="text-center">
        <span className="text-2xl">{template.icon}</span>
        <h2 className="text-lg font-bold mt-2">{q.question}</h2>
        <p className="text-xs text-muted-foreground mt-1">{template.title}</p>
      </div>
      <div className="space-y-2">
        {q.options.map((opt) => (
          <button
            key={opt.id}
            onClick={() => onSelect(opt.id)}
            className="w-full flex items-start gap-3 p-4 rounded-xl border border-border bg-card active:bg-primary/10 transition-colors text-left"
            style={{ minHeight: '56px' }}
            data-testid={`mobile-wizard-option-${opt.id}`}
          >
            <ArrowRight className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <h3 className="font-semibold text-sm">{opt.label}</h3>
              <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{opt.description}</p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
